<template>
  <div class="course-detail">
    <v-card flat>
      <v-card-text class="course-detail__body">
        <div class="course-detail__grid">
          <div class="course-detail__item">
            <div class="course-detail__label">Course Name</div>
            <div class="course-detail__value">
              {{ values.courseName ? values.courseName : "-" }}
            </div>
          </div>
          <div class="course-detail__item">
            <div class="course-detail__label">Course Code</div>
            <div class="course-detail__value">
              <div v-if="values.courseCode" class="course-detail__barcode">
                <barcode :height="50" v-bind:value="values.courseCode">
                  Sorry, rendering failed!
                </barcode>
              </div>
              <span v-else>-</span>
            </div>
          </div>
          <div class="course-detail__item">
            <div class="course-detail__label">Course Owner</div>
            <div class="course-detail__value">
              {{ values.courseOwner ? values.courseOwner.trainingCenterName : "-" }}
            </div>
          </div>
          <div class="course-detail__item">
            <div class="course-detail__label">Course Accreditor</div>
            <div class="course-detail__value">
              {{ values.courseAccreditor ? values.courseAccreditor.accreditorName : "-" }}
            </div>
          </div>
          <div class="course-detail__item">
            <div class="course-detail__label">Accreditation Date</div>
            <div class="course-detail__value">
              {{ values.courseAccreditationDate ? values.courseAccreditationDate : "-" }}
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-card flat class="course-detail__levels">
      <div class="course-detail__section-title">
        Course Levels
        <span class="course-detail__section-count">{{ courseLevels.length }}</span>
      </div>
      <div v-if="courseLevels.length" class="course-detail__levels-table">
        <v-simple-table dense>
          <thead>
          <tr>
            <th>Course Level</th>
            <th>CEU</th>
            <th>Duration</th>
            <th>Passing Score</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(level, index) in courseLevels" :key="level.courseLevelId || index">
            <td>{{ level.courseLevelName }}</td>
            <td>{{ level.continualEducationUnit }}</td>
            <td>{{ level.courseDuration }}</td>
            <td>{{ level.passingScore }}</td>
          </tr>
          </tbody>
        </v-simple-table>
      </div>
      <div v-else class="course-detail__empty">
        No course levels to display.
      </div>
    </v-card>

    <v-card flat class="course-detail__audience">
      <div class="course-detail__section-title">
        Target Audiences
        <span class="course-detail__section-count">{{ audiences.length }}</span>
      </div>
      <div v-if="audiences.length" class="course-detail__audience-list">
        <div v-for="targetAudience in audiences" :key="targetAudience.id" class="course-detail__audience-item">
          <span class="course-detail__audience-dot"></span>
          <span class="course-detail__audience-label">
            {{ targetAudience.health_profession ? targetAudience.health_profession.name : "-" }}
          </span>
        </div>
      </div>
      <div v-else class="course-detail__empty">
        No target audiences to display.
      </div>
    </v-card>

    <v-card flat class="course-detail__content">
      <div class="course-detail__section-title">
        Course Content
      </div>
      <div v-if="hasCourseContent" class="course-detail__content-preview">
        <div class="course-detail__content-actions">
          <v-btn
              class="course-detail__content-action"
              small
              text
              @click.prevent="openCourseContent"
          >
            <v-icon left small>mdi-eye</v-icon>
            Open
          </v-btn>
        </div>
        <iframe
            v-if="supportsInlinePreview && courseContentPreviewUrl"
            class="course-detail__content-frame"
            :src="courseContentPreviewUrl"
            title="Course content preview"
        ></iframe>
        <div v-else class="course-detail__content-file">
          <v-icon color="primary">mdi-file-document-outline</v-icon>
          <div class="course-detail__content-file-copy">
            <div>{{ courseContentFileName }}</div>
            <div v-if="!courseContentPreviewUrl" class="course-detail__content-file-note">
              Preview unavailable here. Use Open to view the uploaded file.
            </div>
          </div>
        </div>
      </div>
      <div v-else class="course-detail__empty">
        No course content uploaded.
      </div>
    </v-card>
  </div>
</template>

<script>
import VueBarcode from 'vue-barcode';
import {buildPreviewUrl, openPresignedUrl} from "@/utils/file";

export default {
  props: {
    course: Object,
  },
  components: {
    'barcode': VueBarcode
  },
  data() {
    return {
      values: {},
      courseContentPreviewUrl: null,
    };
  },
  computed: {
    audiences() {
      return this.values?.courses_target_audiences || [];
    },
    courseLevels() {
      const levels =
          this.values?.courseLevels ||
          this.values?.course_levels ||
          this.values?.courseLevelDetails ||
          this.values?.course_level_details;
      if (Array.isArray(levels) && levels.length) {
        return levels.map((level) => {
          const courseLevel = level.courseLevel || level.course_level;
          const courseLevelId = level.courseLevelId || level.course_level_id || courseLevel?.id;
          const courseLevelName =
              courseLevel?.name ||
              level.courseLevelName ||
              level.course_level_name ||
              (courseLevelId ? `Level ${courseLevelId}` : "-");
          return {
            courseLevelId,
            courseLevelName,
            continualEducationUnit:
                level.continualEducationUnit ?? level.continual_education_unit ?? "-",
            courseDuration: level.courseDuration ?? level.course_duration ?? "-",
            passingScore: level.passingScore ?? level.passing_score ?? "-",
          };
        });
      }

      const fallbackLevelId = this.values?.courseLevelId || this.values?.courseLevel?.id;
      if (
          fallbackLevelId ||
          this.values?.continualEducationUnit ||
          this.values?.courseDuration ||
          this.values?.passingScore
      ) {
        const fallbackName =
            this.values?.courseLevel?.name || (fallbackLevelId ? `Level ${fallbackLevelId}` : "-");
        return [
          {
            courseLevelId: fallbackLevelId || 0,
            courseLevelName: fallbackName,
            continualEducationUnit: this.values?.continualEducationUnit ?? "-",
            courseDuration: this.values?.courseDuration ?? "-",
            passingScore: this.values?.passingScore ?? "-",
          },
        ];
      }

      return [];
    },
    courseContentPath() {
      return this.getCourseContentPath();
    },
    hasCourseContent() {
      return !!this.courseContentPath;
    },
    courseContentExtension() {
      const path = this.courseContentPath;
      if (!path || typeof path !== "string") return "";
      const cleanPath = path.split("?")[0];
      const parts = cleanPath.split(".");
      return parts.length > 1 ? parts.pop().toLowerCase() : "";
    },
    supportsInlinePreview() {
      return this.courseContentExtension === "pdf";
    },
    courseContentFileName() {
      const path = this.courseContentPath;
      if (!path || typeof path !== "string") return "Uploaded document";
      const cleanPath = path.split("?")[0];
      return cleanPath.split("/").pop() || "Uploaded document";
    },
  },
  watch: {
    course: {
      immediate: true,
      handler(value) {
        this.values = value || {};
        this.prefetchCourseContentPreview();
      },
    },
  },
  methods: {
    getCourseContentPath() {
      return (
          this.values?.courseContent ||
          this.values?.course_content ||
          this.values?.courseContentUrl ||
          this.values?.course_content_url ||
          null
      );
    },
    async prefetchCourseContentPreview() {
      const path = this.getCourseContentPath();
      if (!path) {
        this.courseContentPreviewUrl = null;
        return;
      }
      const url = await buildPreviewUrl(path, {
        preferHttps: window.location.protocol === "https:",
        allowInsecure: true,
      });
      this.courseContentPreviewUrl = url || null;
    },
    async openCourseContent() {
      const path = this.getCourseContentPath();
      if (path) {
        await openPresignedUrl(path, {
          preferHttps: window.location.protocol === "https:",
          allowInsecure: true,
        });
      }
    },
  },
};
</script>

<style scoped>
.course-detail__body {
  padding: 16px 20px 24px;
}

.course-detail__grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 12px;
}

@media (min-width: 768px) {
  .course-detail__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.course-detail__item {
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  min-height: 64px;
}

.course-detail__label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 6px;
  display: block;
}

.course-detail__value {
  font-size: 13px;
  color: #0f172a;
}

.course-detail__barcode {
  margin-top: 4px;
}

.course-detail__audience {
  margin-top: 12px;
  padding: 16px 20px 20px;
}

.course-detail__levels {
  margin-top: 12px;
  padding: 16px 20px 20px;
}

.course-detail__content {
  margin-top: 12px;
  padding: 16px 20px 20px;
}

.course-detail__content-preview {
  margin-top: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px;
  background: #f8fafc;
}

.course-detail__content-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 6px;
}

.course-detail__content-frame {
  width: 100%;
  min-height: 320px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
}

.course-detail__content-file {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 72px;
  padding: 12px;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  color: #0f172a;
}

.course-detail__content-file-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.course-detail__content-file-note {
  font-size: 12px;
  color: #64748b;
}

.course-detail__levels-table {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
}

.course-detail__section-title {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.course-detail__section-count {
  background: #e2e8f0;
  color: #334155;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
}

.course-detail__audience-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.course-detail__audience-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  color: #0f172a;
  font-size: 13px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.course-detail__audience-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #1976d2;
}

.course-detail__audience-label {
  white-space: nowrap;
}

.course-detail__empty {
  font-size: 13px;
  color: #64748b;
}
</style>
